import { API_BASE_URL } from "src/config/apiBase";
import {
    ArrowLeft,
    GripHorizontal,
    Image,
    LucideMousePointerClick,
    MessageSquareText,
    PenLine,
    Sparkles,
} from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import TipTapDescriptionBox from "./TipTapDescriptionBox";
import ImageBox from "./ImageBox";
import CategorySelector from "./CategorySelector";
import TagsSelector from "./TagsSelector";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BlogStatusBox2 from "./BlogStatusBox2";
import BlogSeoPanel from "./BlogSeoPanel";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import {
    SECTION_META,
    countWords,
    SectionActions,
    EditorInsertDock,
    makeEmptyRaw,
} from "./blogEditorShared";
import "./BlogAdminTheme.css";
import "./BlogEditor.css";
import "./AddBlog.css";

const AddBlog = () => {
    const [title, setTitle] = useState("");
    const [sections, setSections] = useState([
        { id: 1, type: "description", content: makeEmptyRaw(), },
    ]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const textareaRefs = useRef({});
    const sectionRefs = useRef({});
    const [allCategories, setAllCategory] = useState([])
    const [allTags, setAllTags] = useState([])
    const url = API_BASE_URL
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [featureImage, setFeatureImage] = useState(null);
    const [author, setAuthor] = useState([])
    const [selectedAuthor, setSelectedAuthor] = useState()
    const navigate = useNavigate();
    const token = decryptToken("accessToken") || ""
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [excerpt, setExcerpt] = useState("");
    const [metaTitle, setMetaTitle] = useState("");
    const [metaDescription, setMetaDescription] = useState("");
    const [ogImage, setOgImage] = useState("");
    const [canonicalUrl, setCanonicalUrl] = useState("");
    const [publishedAt, setPublishedAt] = useState("");
    const [featured, setFeatured] = useState(false);

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const reorderedSections = Array.from(sections);
        const [movedSection] = reorderedSections.splice(result.source.index, 1);
        reorderedSections.splice(result.destination.index, 0, movedSection);

        setSections(reorderedSections);
    };

    const fetchCategoriesData = async () => {
        try {
            const response = await fetch(`${url}/blog-category`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            setAllCategory(res.data);
        } catch (error) {
            console.log(error);
        }
    }

    const fetchAuthorData = async () => {
        try {
            const response = await fetch(`${url}/blog-author`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            const authors = Array.isArray(res.data) ? res.data : [];
            setAuthor(authors);

            // ✅ If there's only one author, or saved author exists — preselect it
            if (authors.length === 1) {
                setSelectedAuthor(authors[0]);
                localStorage.setItem("selectedAuthor", JSON.stringify(authors[0]));
            }
            // ✅ Restore previously selected author if any
            else {
                const saved = localStorage.getItem("selectedAuthor");
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const match = authors.find(a => a.id === parsed.id);
                    if (match) setSelectedAuthor(match);
                } else if (authors.length > 0) {
                    // fallback: select first author
                    setSelectedAuthor(authors[0]);
                    localStorage.setItem("selectedAuthor", JSON.stringify(authors[0]));
                }
            }
        } catch (error) {
            console.log(error);
            setAuthor([]);
        }
    };




    const fetchTagData = async () => {
        try {
            const response = await fetch(`${url}/blog-tag`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            setAllTags(res.data);
        } catch (error) {
            console.log(error);
        }
    }

    const addSection = (type, content = {}) => {
        const newSection = {
            id: Date.now(),
            type,
            content: type === "description" ? makeEmptyRaw() : {},
            showTooltip: type === "image" ? false : undefined,
        };

        let updatedSections = [...sections];
        let scrollToSection = false;

        // Check if active section is the 2nd section (index 1) and adding description
        if (type === "description" && activeSectionId === sections[1]?.id && sections.length >= 2) {
            // Add description as the 3rd section (index 2)
            updatedSections.splice(2, 0, newSection);

            // Move existing image section (if any) at index 2 to index 3
            const imageSectionIndex = updatedSections.findIndex(
                (s, i) => i === 2 && s.type === "image"
            );
            if (imageSectionIndex !== -1) {
                const [imageSection] = updatedSections.splice(imageSectionIndex, 1);
                updatedSections.splice(3, 0, imageSection);
            }
            scrollToSection = true;
        } else {
            // Default: Add section at the end
            updatedSections.push(newSection);
            scrollToSection = true; // Scroll to new section for all types
        }

        setSections(updatedSections);

        // Scroll to the newly added section for all types
        if (scrollToSection) {
            setTimeout(() => {
                const sectionElement = sectionRefs.current[newSection.id];
                if (sectionElement) {
                    sectionElement.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100); // Small delay to ensure DOM updates
        }
    };

    const duplicateSection = (id) => {
        const section = sections.find((s) => s.id === id);
        if (section) {
            const newSection = { ...section, id: Date.now() };
            setSections([...sections, newSection]);

            // Scroll to duplicated section for all types
            setTimeout(() => {
                const sectionElement = sectionRefs.current[newSection.id];
                if (sectionElement) {
                    sectionElement.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100);
        }
    };

    const deleteSection = (id) => {
        if (sections.length === 1 && sections[0].type === "description") {
            alert("At least one description section is required.");
            return;
        }
        setSections(sections.filter((s) => s.id !== id));
        if (activeSectionId === id) {
            setActiveSectionId(null);
        }
    };

    const handleImageSelect = (imageObj, sectionId = null) => {
        if (sectionId) {
            setSections(
                sections.map((s) =>
                    s.id === sectionId
                        ? { ...s, content: { ...s.content, url: imageObj.url, title: s.content.title || "" } }
                        : s
                )
            );
        } else {
            addSection("image", { url: imageObj.url, title: "" });
        }
        setIsImageModalOpen(false);
    };

    const handleDescriptionFocus = (sectionId) => {
        setActiveSectionId(sectionId);
    };

    const handleDescriptionBlur = () => {
        setActiveSectionId(null);
    };


    // const handleSubmit = async (statusBoxPayload) => {
    //   if (!title.trim()) {
    //     toast.error("Please enter a blog title.");
    //     return;
    //   }

    //   const finalPayload = {
    //     title,
    //     author_name: selectedAuthor?.title || "",
    //     feature_image: featureImage?.image || null,
    //     sections,
    // categories: selectedCategory,
    //     tags: selectedTags,
    //     status: statusBoxPayload?.status || "Draft",
    //     visibility: statusBoxPayload?.visibility || "Public",
    //     password:
    //       statusBoxPayload?.visibility === "Password"
    //         ? statusBoxPayload.password
    //         : "",
    //   };

    //   try {
    //     const res = await fetch(`${url}/api/blog`, {
    //       method: "POST",
    //       headers: {
    //         "Content-Type": "application/json",
    //       },
    //       body: JSON.stringify(finalPayload),
    //     });

    //     const data = await res.json();

    //     if (res.ok) {
    //         window.location.href = "/all-blogs";

    //       toast.success(
    //         statusBoxPayload?.status === "Publish"
    //           ? "Blog Published Successfully!"
    //           : "Saved as Draft!",
    //         { position: "top-center", autoClose: 2000 }
    //       );

    //     } else {
    //       toast.error("❌ Failed to save blog: " + data.message);
    //       console.log("Server Response →", data);
    //     }
    //   } catch (err) {
    //     console.error(err);
    //     toast.error("Error saving blog. Check console for details.");
    //   }
    // };


    const handleSubmit = async (statusBoxPayload = {}) => {
        if (!title.trim()) {
            toast.error("Please enter a blog title.");
            return null;
        }

        const cleanedSections = sections.map((sec, index) => ({
            ...sec,
            order: index,
            content: sec.content   // already raw object
        }));




        const resolvedStatus =
            statusBoxPayload?.status === "Publish" || statusBoxPayload?.actionType === "publish"
                ? "Publish"
                : statusBoxPayload?.status === "Draft" || statusBoxPayload?.actionType === "draft"
                  ? "Draft"
                  : statusBoxPayload?.status || "Draft";

        const finalPayload = {
            title,
            author_name: selectedAuthor?.title || "",
            feature_image: featureImage?.image || null,
            sections: cleanedSections,
            categories: selectedCategory,
            tags: selectedTags,
            status: resolvedStatus,
            visibility: statusBoxPayload?.visibility || "Public",
            password:
                statusBoxPayload?.visibility === "Password"
                    ? statusBoxPayload.password
                    : "",
            excerpt,
            meta_title: metaTitle,
            meta_description: metaDescription,
            og_image: ogImage || featureImage?.image || null,
            canonical_url: canonicalUrl,
            published_at: publishedAt || null,
            featured,
            action: statusBoxPayload?.actionType || null,
        };

        try {
            const res = await fetch(`${url}/blog`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(finalPayload),
            });

            const data = await res.json();

            if (res.ok) {
                const isPublish =
                    finalPayload.status === "Publish" ||
                    statusBoxPayload?.actionType === "publish";

                if (isPublish) {
                    window.location.href = "/blog";
                    toast.success("Blog Published Successfully!", { position: "top-center", autoClose: 2000 });
                } else {
                    if (statusBoxPayload.actionType === "preview") {
                        window.open(`/admin/preview-blog/${data.data.slug}/${data.data.id || "temp"}`, "_blank");
                        window.location.href = (`/edit-blog/${data.data.id}`)
                    } else {
                        window.location.href = "/blog";
                    }
                    // for draft / preview saves: just show success toast and return server data
                    toast.success("Saved as Draft!", { position: "top-center", autoClose: 1200 });
                }

                return data; 
            } else {
                toast.error("❌ Failed to save blog: " + (data.message || "Server error"));
                console.log("Server Response →", data);
                return null;
            }
        } catch (err) {
            console.error(err);
            toast.error("Error saving blog. Check console for details.");
            return null;
        }
    };

    useEffect(() => {
        fetchCategoriesData();
        fetchTagData();
        fetchAuthorData();
    }, [])

    const wordCount = countWords(sections);

    return (
        <div className="add-blog-page">
            <header className="add-blog-hero">
                <div className="add-blog-hero-top">
                    <button type="button" className="add-blog-back" onClick={() => navigate("/blog")}>
                        <ArrowLeft size={16} />
                        All blogs
                    </button>
                    <div className="add-blog-hero-meta">
                        <span className="add-blog-pill">
                            <Sparkles size={12} style={{ display: "inline", marginRight: 4, verticalAlign: -2 }} />
                            New story
                        </span>
                        <span className="add-blog-stat">{sections.length} blocks</span>
                        <span className="add-blog-stat">{wordCount} words</span>
                    </div>
                </div>
                <div className="add-blog-title-wrap">
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Your headline goes here…"
                        className="add-blog-title-input"
                        aria-label="Blog title"
                    />
                    <p className="add-blog-title-hint">
                        <PenLine size={12} style={{ display: "inline", marginRight: 4, verticalAlign: -1 }} />
                        A strong title helps readers discover your post
                    </p>
                </div>
            </header>

            <div className="add-blog-layout">
                <main className="add-blog-canvas">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="sections">
                            {(provided) => (
                                <div className="add-blog-timeline" {...provided.droppableProps} ref={provided.innerRef}>
                                {sections.map((section, index) => (
                                    <Draggable key={section.id} draggableId={String(section.id)} index={index}>
                                        {(provided) => (
                                            <div
                                                ref={(el) => {
                                                    provided.innerRef(el);
                                                    sectionRefs.current[section.id] = el;
                                                }}
                                                {...provided.draggableProps}
                                                className={`add-blog-section add-blog-section--${SECTION_META[section.type]?.typeClass || "description"}`}
                                            >
                                                <div className="add-blog-section-head">
                                                    <div className="add-blog-section-label">
                                                        <span className="add-blog-section-index">{index + 1}</span>
                                                        {SECTION_META[section.type]?.label || section.type}
                                                    </div>
                                                    <div className="add-blog-drag" {...provided.dragHandleProps}>
                                                        <GripHorizontal size={18} />
                                                    </div>
                                                </div>
                                                <div className="add-blog-section-body">
                                                {section.type === "description" && (
                                                    <TipTapDescriptionBox
                                                            content={section.content}
                                                            onChange={(value) => {
                                                                setSections((prev) =>
                                                                    prev.map((s) => {
                                                                        if (s.id !== section.id) return s;
                                                                        if (JSON.stringify(s.content) === JSON.stringify(value)) return s;
                                                                        return { ...s, content: value };
                                                                    })
                                                                );
                                                            }}
                                                            textareaRef={(el) => (textareaRefs.current[section.id] = el)}
                                                            onFocus={() => handleDescriptionFocus(section.id)}
                                                            onBlur={handleDescriptionBlur}
                                                        />
                                                )}
                                                {section.type === "image" && (
                                                    <>
                                                        <input
                                                            type="text"
                                                            value={section.content?.title || ""}
                                                            onChange={(e) => {
                                                                setSections(
                                                                    sections.map((s) =>
                                                                        s.id === section.id
                                                                            ? { ...s, content: { ...s.content, title: e.target.value } }
                                                                            : s
                                                                    )
                                                                );
                                                            }}
                                                            placeholder="Image caption (optional)"
                                                            className="add-blog-field-input mb-4"
                                                        />
                                                        <ImageBox
                                                            selectedImages={section.content.images || []}
                                                            mode="multi"
                                                            onSelect={(arr) => {
                                                                setSections((prev) =>
                                                                    prev.map((s) =>
                                                                        s.id === section.id
                                                                            ? { ...s, content: { ...s.content, images: arr } }
                                                                            : s
                                                                    )
                                                                );
                                                            }}
                                                            onDelete={(idx) => {
                                                                if (idx === undefined) {
                                                                    setSections((prev) =>
                                                                        prev.map((s) =>
                                                                            s.id === section.id
                                                                                ? { ...s, content: { ...s.content, images: [] } }
                                                                                : s
                                                                        )
                                                                    );
                                                                    return;
                                                                }
                                                                const newArr = (section.content.images || []).filter((_, i) => i !== idx);
                                                                setSections((prev) =>
                                                                    prev.map((s) =>
                                                                        s.id === section.id
                                                                            ? { ...s, content: { ...s.content, images: newArr } }
                                                                            : s
                                                                    )
                                                                );
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                {section.type === "button" && (
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                                                            <input
                                                                type="text"
                                                                value={section.content?.text || ""}
                                                                onChange={(e) => {
                                                                    setSections(
                                                                        sections.map((s) =>
                                                                            s.id === section.id ? { ...s, content: { ...s.content, text: e.target.value } } : s
                                                                        )
                                                                    );
                                                                }}
                                                                placeholder="Button label"
                                                                className="add-blog-field-input"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={section.content?.action || ""}
                                                                onChange={(e) => {
                                                                    setSections(
                                                                        sections.map((s) =>
                                                                            s.id === section.id ? { ...s, content: { ...s.content, action: e.target.value } } : s
                                                                        )
                                                                    );
                                                                }}
                                                                placeholder="Link URL"
                                                                className="add-blog-field-input"
                                                            />
                                                        </div>
                                                )}
                                                </div>
                                                <SectionActions
                                                    onDuplicate={() => duplicateSection(section.id)}
                                                    onDelete={() => deleteSection(section.id)}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {isImageModalOpen && (
                        <ImageBox
                            selectedImage={{ image: "" }}
                            onSelect={(img) => {
                                handleImageSelect(img);
                                setIsImageModalOpen(false);
                            }}
                            onDelete={() => setIsImageModalOpen(false)}
                            onClose={() => setIsImageModalOpen(false)}
                        />
                    )}
                </main>

                <aside className="add-blog-deck">
                    <div className="add-blog-deck-sticky">
                        <div className="add-blog-deck-card add-blog-deck-card--publish">
                            <div className="add-blog-deck-card-head">Ready to publish?</div>
                            <div className="add-blog-deck-card-body">
                                <BlogStatusBox2 onSuccess={handleSubmit} />
                            </div>
                        </div>

                        <div className="add-blog-deck-card">
                            <div className="add-blog-deck-card-body">
                                <BlogSeoPanel
                                    excerpt={excerpt}
                                    setExcerpt={setExcerpt}
                                    metaTitle={metaTitle}
                                    setMetaTitle={setMetaTitle}
                                    metaDescription={metaDescription}
                                    setMetaDescription={setMetaDescription}
                                    ogImage={ogImage}
                                    setOgImage={setOgImage}
                                    canonicalUrl={canonicalUrl}
                                    setCanonicalUrl={setCanonicalUrl}
                                    publishedAt={publishedAt}
                                    setPublishedAt={setPublishedAt}
                                    featured={featured}
                                    setFeatured={setFeatured}
                                />
                            </div>
                        </div>

                        <div className="add-blog-deck-card">
                            <div className="add-blog-deck-card-body">
                                <AuthorSelect
                                    authors={author}
                                    selectedAuthor={selectedAuthor}
                                    onSelect={(a) => setSelectedAuthor(a)}
                                    fetchAuthors={fetchAuthorData}
                                />
                            </div>
                        </div>

                        <div className="add-blog-deck-card">
                            <div className="add-blog-deck-card-head">Cover image</div>
                            <div className="add-blog-deck-card-body">
                                <ImageBox
                                    selectedImage={featureImage}
                                    mode="single"
                                    onSelect={(img) => setFeatureImage(img)}
                                    onDelete={() => setFeatureImage(null)}
                                />
                            </div>
                        </div>

                        <div className="add-blog-deck-card">
                            <div className="add-blog-deck-card-body">
                                <CategorySelector
                                    categories={allCategories}
                                    onSelect={setSelectedCategory}
                                    selected={selectedCategory}
                                />
                            </div>
                        </div>

                        <div className="add-blog-deck-card">
                            <div className="add-blog-deck-card-body">
                                <TagsSelector tags={allTags} onChange={setSelectedTags} />
                            </div>
                        </div>
                    </div>
                </aside>
            </div>

            <EditorInsertDock addSection={addSection} />
        </div>
    );
};

const AuthorSelect = ({ authors = [], selectedAuthor, onSelect, fetchAuthors }) => {
    const [search, setSearch] = useState("");
    const [filteredAuthors, setFilteredAuthors] = useState(authors);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // ✅ Fetch authors if not loaded
    useEffect(() => {
        if (!authors.length && fetchAuthors) fetchAuthors();
    }, [authors, fetchAuthors]);

    // ✅ Automatically select first author if only one exists
    useEffect(() => {
        if (authors.length === 1 && !selectedAuthor) {
            onSelect(authors[0]);
        }
    }, [authors, selectedAuthor, onSelect]);

    // ✅ Filter authors on search
    useEffect(() => {
        if (authors.length > 0) {
            setFilteredAuthors(
                authors.filter((a) =>
                    a.title.toLowerCase().includes(search.toLowerCase())
                )
            );
        }
    }, [search, authors]);

    // ✅ Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (author) => {
        onSelect(author);
        setIsOpen(false);
        setSearch("");
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <label className="add-blog-author-label">Author</label>
            <div
                onClick={() => setIsOpen((prev) => !prev)}
                className="add-blog-author-trigger"
            >
                <span>{selectedAuthor ? selectedAuthor.title : "Select author"}</span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {/* Dropdown menu */}
            {isOpen && (
                <div className="add-blog-author-menu">
                    {filteredAuthors.length > 0 ? (
                        filteredAuthors.map((author) => (
                            <div
                                key={author.id}
                                onClick={() => handleSelect(author)}
                                className={`add-blog-author-option ${selectedAuthor?.id === author.id ? "is-selected" : ""}`}
                            >
                                {author.title}
                            </div>
                        ))
                    ) : (
                        <p className="add-blog-author-empty">No authors found</p>
                    )}
                </div>
            )}
        </div>
    );
};



export default AddBlog;